import { Channel } from 'laravel-echo/dist/channel'
import axios, { AxiosError, AxiosInstance } from 'axios'
import { getApp } from '@/main'
import store from '@/store'
import { UserGetters } from '@/store/modules/user/types'
import { responseChannel } from '../../../websocket/channels'
import { asyncResponseEvent } from '../../../websocket/events'
import { ProjectID } from '@/types/platform'

export const attachAxiosWSResponseInterceptor = (instance: AxiosInstance) => {
  const wsChannels: Record<string, Channel> = {}

  instance.interceptors.request.use(
    (config) => {
      const projectId =
        config.headers?.['X-Project-ID'] ?? store.getters.getProjectId

      const projectUserId = (
        store.getters['user/getUserId'] as UserGetters['getUserId']
      )(projectId)

      if (config.useWS && config.url && config.headers) {
        if (projectId === ProjectID.im) {
          delete config.headers['X-Project-ID']
        } else {
          config.headers['X-Project-ID'] = projectId
        }

        config.headers['X-Project-User-ID'] = projectUserId

        const { model, itemId } = config.useWS

        config.headers['X-Async-Key'] = `${model},${itemId}`
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    (response) => {
      const requestId = response.config?.headers?.['X-Request-ID']?.toString()
      const projectId = response.config?.headers?.['X-Project-ID']?.toString()
      const asyncKey = response.config?.headers?.['X-Async-Key']?.toString()

      const useWS = !!response.config.useWS
      const isAsyncResponse = response.config.useWS?.isAsyncResponse ?? true

      if (isAsyncResponse && requestId && projectId && asyncKey && useWS) {
        return new Promise((resolve, reject) => {
          const echoChannelName = responseChannel({
            projectId,
            key: asyncKey,
          })

          getApp.then((app) => {
            const errorTimeoutData: {
              id: number
              data: {
                code: number
                body: unknown
              }
            } = {
              id: 0,
              data: {
                code: 0,
                body: null,
              },
            }

            let isFinishedAsyncResponse = false
            let asyncResponseApiTimeoutId = 0

            const url = `/api/v1/async-response/${requestId}/`

            const fallbackTimeoutId = window.setTimeout(() => {
              const getAsyncResponse = () => {
                if (isFinishedAsyncResponse) return

                instance
                  .get(url, {
                    useRetry: true,
                  })
                  .then(({ data: { body, code } }) => {
                    errorTimeoutData.data = {
                      body,
                      code,
                    }

                    if (code === 202) {
                      if (!errorTimeoutData.id) {
                        errorTimeoutData.id = window.setTimeout(() => {
                          executor(
                            errorTimeoutData.data.code,
                            errorTimeoutData.data.body
                          )
                        }, 5 * 60e3)
                      }

                      asyncResponseApiTimeoutId = window.setTimeout(() => {
                        getAsyncResponse()
                      }, 3e3)
                    } else {
                      executor(code, body)
                    }
                  })
                  .catch((error) => {
                    if (axios.isCancel(error)) {
                      reject(error)
                    } else {
                      executor(error.response?.status, error.response?.data)
                    }
                  })
              }

              getAsyncResponse()
            }, 3e3)

            const executor = (code: number, body: unknown) => {
              wsChannels[echoChannelName].stopListening(
                asyncResponseEvent,
                wsChannelListenner
              )
              window.clearTimeout(fallbackTimeoutId)
              window.clearTimeout(errorTimeoutData.id)
              window.clearTimeout(asyncResponseApiTimeoutId)

              if (isFinishedAsyncResponse) return

              isFinishedAsyncResponse = true

              if (code !== 202 && code > 199 && code < 300) {
                resolve({
                  ...response,
                  data: body,
                })
              } else {
                reject(
                  new AxiosError(
                    `${code} Async Response`,
                    code.toString(),
                    response.config,
                    response.request,
                    {
                      ...response,
                      data: body,
                      status: code,
                    }
                  )
                )
              }
            }

            const wsChannelListenner = ({
              code,
              body,
              request_id: requestIdFromWS,
            }: {
              code: number
              body: unknown
              request_id: string
              socketId: string
            }) => {
              if (requestId !== requestIdFromWS) return

              executor(code, body)
            }

            if (!wsChannels[echoChannelName]) {
              wsChannels[echoChannelName] = app.$echo.channel(echoChannelName)
            }

            wsChannels[echoChannelName].listen(
              asyncResponseEvent,
              wsChannelListenner
            )
          })
        })
      }

      return response
    },
    (error: AxiosError) => {
      return Promise.reject(error)
    }
  )
}
